/**
 * FAQ 등록/수정 페이지
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Form, Button, Spinner, Tabs, Tab } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import RichTextEditor from '../../components/common/RichTextEditor';
import { faqService } from '../../services/faqService';
import { FAQ } from '../../types/faq';

const FAQForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isNew = location.pathname.includes('/new');
  const faqId = isNew ? 0 : (id ? parseInt(id, 10) : 0);

  const [activeTab, setActiveTab] = useState<'ko' | 'en' | 'th'>('ko');
  const [formData, setFormData] = useState<Partial<FAQ>>({
    category: '',
    question: { ko: '', en: '', th: '' },
    answer: { ko: '', en: '', th: '' },
    display_order: 0,
    status: 'active',
  });

  const { data: faq, isLoading } = useQuery({
    queryKey: ['faq', faqId],
    queryFn: () => faqService.getFAQ(faqId),
    enabled: !!faqId && faqId > 0 && !isNew,
  });

  const saveMutation = useMutation({
    mutationFn: (data: Partial<FAQ>) =>
      isNew ? faqService.createFAQ(data) : faqService.updateFAQ(faqId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
      alert(isNew ? 'FAQ가 등록되었습니다.' : 'FAQ가 수정되었습니다.');
      navigate('/admin/faqs');
    },
    onError: (error: any) => {
      alert(`저장 실패: ${error.message}`);
    },
  });

  useEffect(() => {
    if (faq) {
      setFormData({
        category: faq.category || '',
        question: faq.question || { ko: '', en: '', th: '' },
        answer: faq.answer || { ko: '', en: '', th: '' },
        display_order: faq.display_order,
        status: faq.status,
      });
    }
  }, [faq]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 한국어 질문과 답변은 필수
    if (!formData.question?.ko?.trim() || !formData.answer?.ko?.trim()) {
      alert('한국어 질문과 답변은 필수입니다.');
      return;
    }

    saveMutation.mutate(formData);
  };

  const handleQuestionChange = (lang: 'ko' | 'en' | 'th', value: string) => {
    setFormData((prev) => ({
      ...prev,
      question: {
        ...prev.question,
        [lang]: value,
      },
    }));
  };

  const handleAnswerChange = (lang: 'ko' | 'en' | 'th', value: string) => {
    setFormData((prev) => ({
      ...prev,
      answer: {
        ...prev.answer,
        [lang]: value,
      },
    }));
  };

  if (isLoading && !isNew) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>{isNew ? 'FAQ 등록' : 'FAQ 수정'}</h1>
          <Button variant="secondary" onClick={() => navigate('/admin/faqs')}>
            목록으로
          </Button>
        </div>

        <Form onSubmit={handleSubmit}>
          <Card className="mb-4">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>카테고리</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.category || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  placeholder="예: 예약, 결제, 환불 등"
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>표시 순서</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formData.display_order || 0}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      display_order: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
                <Form.Text style={{ color: '#888' }}>숫자가 작을수록 먼저 표시됩니다.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>상태</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value as 'active' | 'inactive',
                    }))
                  }
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                >
                  <option value="active">활성</option>
                  <option value="inactive">비활성</option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k as 'ko' | 'en' | 'th')}
                className="mb-3"
              >
                <Tab eventKey="ko" title="한국어 (필수)">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>질문 (한국어) *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.question?.ko || ''}
                      onChange={(e) => handleQuestionChange('ko', e.target.value)}
                      placeholder="질문을 입력하세요..."
                      required
                      style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>답변 (한국어) *</Form.Label>
                    <RichTextEditor
                      value={formData.answer?.ko || ''}
                      onChange={(value) => handleAnswerChange('ko', value)}
                      placeholder="답변을 입력하세요..."
                      height="400px"
                    />
                  </Form.Group>
                </Tab>

                <Tab eventKey="en" title="English">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>Question (English)</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.question?.en || ''}
                      onChange={(e) => handleQuestionChange('en', e.target.value)}
                      placeholder="Enter question..."
                      style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>Answer (English)</Form.Label>
                    <RichTextEditor
                      value={formData.answer?.en || ''}
                      onChange={(value) => handleAnswerChange('en', value)}
                      placeholder="Enter answer..."
                      height="400px"
                    />
                  </Form.Group>
                </Tab>

                <Tab eventKey="th" title="ไทย">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>คำถาม (ไทย)</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.question?.th || ''}
                      onChange={(e) => handleQuestionChange('th', e.target.value)}
                      placeholder="กรอกคำถาม..."
                      style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>คำตอบ (ไทย)</Form.Label>
                    <RichTextEditor
                      value={formData.answer?.th || ''}
                      onChange={(value) => handleAnswerChange('th', value)}
                      placeholder="กรอกคำตอบ..."
                      height="400px"
                    />
                  </Form.Group>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={() => navigate('/admin/faqs')}>
              취소
            </Button>
            <Button variant="primary" type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  저장 중...
                </>
              ) : (
                '저장'
              )}
            </Button>
          </div>
        </Form>
      </div>
    </AdminLayout>
  );
};

export default FAQForm;

